import { apiUrl } from '@ai-studio/shared';

import { api } from './api.js';
import { compositeFrame, parsePixelDoc } from './pixel-doc.js';

/**
 * Asset previews for the Assets panel and asset fields:
 *
 * - images: the file itself;
 * - pixel art (`*.pixel.json`): first frame composited in the browser;
 * - models, `.glb`, materials (on a sphere) and prefabs: rendered by the
 *   engine on the server (`GET /api/projects/:id/thumbnail`, cached in the
 *   project's `.prism/thumbnails/`);
 * - audio clips: waveform from the browser's audio decoder.
 *
 * Client results are cached per path + modification time.
 */

export type ThumbKind = 'image' | 'pixel' | 'render' | 'waveform' | 'none';

export function thumbKind(path: string): ThumbKind {
  const p = path.toLowerCase();
  if (/\.(png|jpe?g|webp|gif|bmp)$/.test(p)) return 'image';
  if (p.endsWith('.pixel.json')) return 'pixel';
  if (/\.(model\.json|glb|gltf|material\.json|prefab\.json)$/.test(p)) return 'render';
  if (/\.(wav|ogg|mp3|flac)$/.test(p)) return 'waveform';
  return 'none';
}

export function rawUrl(projectId: number, path: string, version?: string): string {
  return apiUrl(`/projects/${projectId}/raw?path=${encodeURIComponent(path)}${version ? `&v=${encodeURIComponent(version)}` : ''}`);
}

/** Engine-rendered preview (PNG); `version` busts the browser cache when the file changes. */
export function renderThumbUrl(projectId: number, path: string, size: number, version?: string): string {
  return apiUrl(
    `/projects/${projectId}/thumbnail?path=${encodeURIComponent(path)}&size=${size}${version ? `&v=${encodeURIComponent(version)}` : ''}`,
  );
}

/** Engine render of a model from an orbit position (angle = turntable yaw, pitch in degrees); the server snaps it to a cache grid. */
export function renderOrbitUrl(projectId: number, path: string, size: 256 | 512, orbit: { angle: number; pitch: number }, version?: string): string {
  const v = version ? `&v=${encodeURIComponent(version)}` : '';
  return apiUrl(`/projects/${projectId}/thumbnail?path=${encodeURIComponent(path)}&size=${size}&angle=${orbit.angle}&pitch=${orbit.pitch}${v}`);
}

/** Thumbnail sizes the server renders (requests snap up to one of these, so the cache stays small). */
export const RENDER_SIZES = [64, 128, 256] as const;

export function renderSize(px: number): number {
  const want = px * (globalThis.devicePixelRatio || 1);
  return RENDER_SIZES.find((s) => s >= want) ?? RENDER_SIZES[RENDER_SIZES.length - 1]!;
}

/* ------------------------------------------------------------------ */
/* Client-side previews: a small queue so a folder of clips does not   */
/* decode everything at once.                                          */

const cache = new Map<string, Promise<string | null>>();
let running = 0;
const queue: (() => void)[] = [];

function limited<T>(job: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const start = () => {
      running++;
      job()
        .then(resolve, reject)
        .finally(() => {
          running--;
          queue.shift()?.();
        });
    };
    if (running < 2) start();
    else queue.push(start);
  });
}

/** Data URL of a pixel-art document's first frame (null if unreadable). */
export function pixelThumb(projectId: number, path: string, version: string): Promise<string | null> {
  const key = `px:${projectId}:${path}:${version}`;
  let p = cache.get(key);
  if (!p) {
    p = limited(async () => {
      try {
        const res = await api.get<{ file: { content: string } }>(`/projects/${projectId}/file?path=${encodeURIComponent(path)}`);
        const doc = parsePixelDoc(res.file.content);
        const pixels = compositeFrame(doc, 0);
        const canvas = document.createElement('canvas');
        canvas.width = doc.width;
        canvas.height = doc.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.putImageData(new ImageData(new Uint8ClampedArray(pixels), doc.width, doc.height), 0, 0);
        return canvas.toDataURL('image/png');
      } catch {
        return null;
      }
    });
    cache.set(key, p);
  }
  return p;
}

/**
 * Min/max peaks of an audio clip (`columns` pairs in -1..1), as an SVG path
 * in a `columns` × 2 box centred on y = 1 (null if it cannot be decoded).
 */
export function waveformPath(projectId: number, path: string, version: string, columns = 64): Promise<string | null> {
  const key = `wf:${projectId}:${path}:${version}:${columns}`;
  let p = cache.get(key);
  if (!p) {
    p = limited(async () => {
      try {
        const res = await fetch(rawUrl(projectId, path, version), { credentials: 'same-origin' });
        if (!res.ok) return null;
        const bytes = await res.arrayBuffer();
        const Ctx = globalThis.OfflineAudioContext;
        if (!Ctx) return null;
        const audio = await new Ctx(1, 1, 22050).decodeAudioData(bytes);
        return peaksPath(audio, columns);
      } catch {
        return null;
      }
    });
    cache.set(key, p);
  }
  return p;
}

function peaksPath(audio: AudioBuffer, columns: number): string {
  const channels = Array.from({ length: audio.numberOfChannels }, (_, i) => audio.getChannelData(i));
  const length = audio.length;
  const per = Math.max(1, Math.floor(length / columns));
  const tops: number[] = [];
  const bottoms: number[] = [];
  let peak = 1e-6;
  for (let c = 0; c < columns; c++) {
    let lo = 0;
    let hi = 0;
    const end = Math.min(length, (c + 1) * per);
    // Stride through long clips: the outline only needs the envelope.
    const step = Math.max(1, Math.floor(per / 512));
    for (let i = c * per; i < end; i += step) {
      for (const data of channels) {
        const v = data[i]!;
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
    }
    tops.push(hi);
    bottoms.push(lo);
    peak = Math.max(peak, hi, -lo);
  }
  const y = (v: number) => (1 - v / peak).toFixed(3);
  let d = `M0 ${y(tops[0] ?? 0)}`;
  for (let c = 1; c < columns; c++) d += `L${c} ${y(tops[c]!)}`;
  for (let c = columns - 1; c >= 0; c--) d += `L${c} ${y(bottoms[c]!)}`;
  return `${d}Z`;
}
